import * as React from 'react';
import { atom, createStore, Provider, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { convertCustomTagToDimension } from '../utils';
import type * as FilterTypes from '../../types';

export interface FilterDialogContextProps<
  T extends FilterTypes.FilterValue = FilterTypes.FilterValue,
  S extends FilterTypes.SavedFilter = FilterTypes.SavedFilter,
> {
  meta?: FilterTypes.FilterMeta;
  dimensionLookup?: Record<string, string>;
  presetFilters?: Pick<S, 'name' | 'rule' | 'rule_text' | 'fid'>[];
  /**
   * Whether the filter dialog is disabled.
   * If true, the most of functions will be disabled, includes "saving", "filter builder", "editing", etc.,
   * but the "remove" button (includes each atom) will still be enabled.
   * @default false
   */
  disabled?: boolean;
  compareMode?: boolean;
  compareSeries?: boolean;
  value?: T;
  advancedFilterEnabled?: boolean;
  advancedFilterValue?: FilterTypes.FilterRule[][];
  defaultRulesExpanded?: boolean;
  defaultAppendedAtomStatus?: 'dimension' | 'filter';
  onChange?: (value: T) => void;
  onCustomTagSearch?: (
    keywords?: string,
  ) => Promise<FilterTypes.FilterSuggestionResponse[]> | FilterTypes.FilterSuggestionResponse[];
  onDimensionValueSearch?: (
    keywords?: string,
    apiUrl?: string,
  ) => Promise<FilterTypes.FilterSuggestionResponse[]> | FilterTypes.FilterSuggestionResponse[];
  onSavedFilterSearch?: (
    keywords?: string,
  ) => Promise<(S & Required<Pick<S, 'matchSource'>>)[]> | (S & Required<Pick<S, 'matchSource'>>)[];
  onRecentFilterSearch?: () => Promise<S[]> | S[];
  onFilterSave?: (filter: FilterTypes.Filter) => Promise<S>;
  onCompareSeriesChange?: (value: boolean) => void;
  onRulesExpandChange?: (value: boolean) => void;
  onAppendedAtomStatusChange?: (
    value: NonNullable<FilterDialogContextProps['defaultAppendedAtomStatus']>,
  ) => void;
  onAdvancedFilterAtomChange?: (value: FilterTypes.FilterRule, operator: string) => void;
  onAdvancedFilterAtomRemove?: (value: FilterTypes.FilterRule) => void;
  onExitAdvancedFilter?: () => void;
  onResetAdvancedFilter?: () => void;
}

type FilterDialogInnerStatus = 'idle' | 'editing' | 'editing-advanced' | 'simple-saving';
type AppendedAtomStatus = 'dimension' | 'filter';

export interface FilterDialogProviderRef<
  T extends FilterTypes.FilterValue = FilterTypes.FilterValue,
> {
  getInnerStatus: () => FilterDialogInnerStatus;
  getInnerFilterValue: () => T;
  updateInnerStatus: (
    value:
      | FilterDialogInnerStatus
      | ((current: FilterDialogInnerStatus) => FilterDialogInnerStatus),
  ) => void;
  updateInnerFilterValue: (value: T | ((current: T) => T)) => void;
}

const DEFAULT_FILTER_VALUE: FilterTypes.FilterValue = { rule: [] };
const DEFAULT_FILTER_RULES_EXPANDED = true;
const DEFAULT_SIMPLE_MODE_SELECT_VALUE = 'dimension';
const DEFAULT_READONLY_CONTEXT_VALUE: Omit<FilterDialogContextProps, 'value'> &
  Required<Pick<FilterDialogContextProps, 'meta'>> = {
  meta: {
    dimensions: [],
    categories: {},
  },
};

const filterReadonlyContextAtom = atom(DEFAULT_READONLY_CONTEXT_VALUE);
const defaultFilterValue = atom(DEFAULT_FILTER_VALUE);

const filterValueAtom = atom(DEFAULT_FILTER_VALUE);
const filterStatusAtom = atom<FilterDialogInnerStatus>('idle');
const appendedAtomStatus = atom<AppendedAtomStatus>('filter');
const rulesExpandedAtom = atom(DEFAULT_FILTER_RULES_EXPANDED);
const loadingAtom = atom(false);

// Generate finer-grained atoms to avoid unnecessary re-render
const filterValueValidatedAtom = atom(
  (get) =>
    !get(filterValueAtom)
      .rule.flat(2)
      .some((it) => it.display === '' && it.value === ''),
);
const invalidCustomTagFilterWarningAtom = atom((get) =>
  get(filterValueAtom)
    .rule.flat(2)
    .some((it) => it.field === 'TAG'),
);
const advancedFilterValueAtom = atom((get) => get(filterReadonlyContextAtom).advancedFilterValue);
const advancedFilterEnabledAtom = atom(
  (get) => get(filterReadonlyContextAtom).advancedFilterEnabled,
);
const filterMetaAtom = atom((get) => get(filterReadonlyContextAtom).meta);
const filterMetaMapAtom = atom(
  (get) =>
    new Map<string, FilterTypes.DimensionFieldMeta>(
      (
        get(filterMetaAtom)
          .dimensions.map((it) => it.fields)
          .flat(Infinity) as FilterTypes.DimensionFieldMeta[]
      ).map((it) => [it.value, it]),
    ),
);
const filterDimensionLookupAtom = atom((get) => get(filterReadonlyContextAtom).dimensionLookup);
const presetFiltersAtom = atom((get) => get(filterReadonlyContextAtom).presetFilters);
const filterDisabledAtom = atom((get) => get(filterReadonlyContextAtom).disabled);
const filterCompareModeAtom = atom((get) => get(filterReadonlyContextAtom).compareMode);
const filterCompareSeriesAtom = atom((get) => get(filterReadonlyContextAtom).compareSeries);
const filterOnChangeAtom = atom((get) => get(filterReadonlyContextAtom).onChange);
const filterOnCustomTagSearchAtom = atom((get) => get(filterReadonlyContextAtom).onCustomTagSearch);
const filterOnDimensionValueSearchAtom = atom(
  (get) => get(filterReadonlyContextAtom).onDimensionValueSearch,
);
const filterOnSavedFilterSearchAtom = atom(
  (get) => get(filterReadonlyContextAtom).onSavedFilterSearch,
);
const filterOnRecentFilterSearchAtom = atom(
  (get) => get(filterReadonlyContextAtom).onRecentFilterSearch,
);
const filterOnFilterSaveAtom = atom((get) => get(filterReadonlyContextAtom).onFilterSave);
const filterOnCompareSeriesChangeAtom = atom(
  (get) => get(filterReadonlyContextAtom).onCompareSeriesChange,
);
const filterOnAdvancedFilterAtomChangeAtom = atom(
  (get) => get(filterReadonlyContextAtom).onAdvancedFilterAtomChange,
);
const filterOnAdvancedFilterAtomRemoveAtom = atom(
  (get) => get(filterReadonlyContextAtom).onAdvancedFilterAtomRemove,
);
const filterOnExitAdvancedFilterAtom = atom(
  (get) => get(filterReadonlyContextAtom).onExitAdvancedFilter,
);
const filterOnResetAdvancedFilterAtom = atom(
  (get) => get(filterReadonlyContextAtom).onResetAdvancedFilter,
);

// Root store read/write atoms hooks
export const useFilterValue = () => useAtom(filterValueAtom);
export const useFilterStatus = () => useAtom(filterStatusAtom);
export const useLoading = () => useAtom(loadingAtom);
export const useAppendedAtomStatus = () => useAtom(appendedAtomStatus);
export const useRulesExpanded = () => useAtom(rulesExpandedAtom);

// Root store readonly atoms hooks
export const useFilterValueValidated = () => useAtomValue(filterValueValidatedAtom);
export const useInvalidCustomTagFilterWarning = () =>
  useAtomValue(invalidCustomTagFilterWarningAtom);
export const useAdvancedFilterValue = () => useAtomValue(advancedFilterValueAtom);
export const useAdvancedFilterEnabled = () => useAtomValue(advancedFilterEnabledAtom);
export const useFilterMeta = () => useAtomValue(filterMetaAtom);
export const useFilterMetaMap = () => useAtomValue(filterMetaMapAtom);
export const useFilterDimensionLookup = () => useAtomValue(filterDimensionLookupAtom);
export const usePresetFilters = () => useAtomValue(presetFiltersAtom);
export const useFilterDisabled = () => useAtomValue(filterDisabledAtom);
export const useFilterCompareMode = () => useAtomValue(filterCompareModeAtom);
export const useFilterCompareSeries = () => useAtomValue(filterCompareSeriesAtom);
export const useOnChange = () => useAtomValue(filterOnChangeAtom);
export const useOnCustomTagSearch = () => useAtomValue(filterOnCustomTagSearchAtom);
export const useOnDimensionValueSearch = () => useAtomValue(filterOnDimensionValueSearchAtom);
export const useOnSavedFilterSearch = () => useAtomValue(filterOnSavedFilterSearchAtom);
export const useOnRecentFilterSearch = () => useAtomValue(filterOnRecentFilterSearchAtom);
export const useOnFilterSave = () => useAtomValue(filterOnFilterSaveAtom);
export const useOnCompareSeriesChange = () => useAtomValue(filterOnCompareSeriesChangeAtom);
export const useOnAdvancedFilterAtomChange = () =>
  useAtomValue(filterOnAdvancedFilterAtomChangeAtom);
export const useOnAdvancedFilterAtomRemove = () =>
  useAtomValue(filterOnAdvancedFilterAtomRemoveAtom);
export const useOnExitAdvancedFilter = () => useAtomValue(filterOnExitAdvancedFilterAtom);
export const useOnResetAdvancedFilter = () => useAtomValue(filterOnResetAdvancedFilterAtom);

// Root store actions
export const useResetFilterValue = () => {
  const value = useAtomValue(defaultFilterValue);
  const setValue = useSetAtom(filterValueAtom);
  const defaultValueRef = React.useRef(value);
  defaultValueRef.current = value;

  return React.useCallback(() => {
    setValue(defaultValueRef.current);
  }, [setValue]);
};

// Root store Provider
export const FilterDialogProvider = React.forwardRef<
  FilterDialogProviderRef,
  FilterDialogContextProps & { children: React.ReactNode }
>((props, ref) => {
  const {
    children,
    value = DEFAULT_FILTER_VALUE,
    meta = DEFAULT_READONLY_CONTEXT_VALUE.meta,
    defaultRulesExpanded = DEFAULT_FILTER_RULES_EXPANDED,
    defaultAppendedAtomStatus = DEFAULT_SIMPLE_MODE_SELECT_VALUE,
    advancedFilterValue,
    onRulesExpandChange,
    onAppendedAtomStatusChange,
    ...p
  } = { ...DEFAULT_READONLY_CONTEXT_VALUE, ...props };
  const store = React.useRef<ReturnType<typeof createStore>>();
  const filterDimensionSet = React.useMemo(
    () =>
      new Set(
        (
          meta.dimensions.map((it) => it.fields).flat(Infinity) as FilterTypes.DimensionFieldMeta[]
        ).map((it) => it.value),
      ),
    [meta.dimensions],
  );
  const convertedValue = React.useMemo(
    () => ({ ...value, rule: convertCustomTagToDimension(filterDimensionSet, value.rule) }),
    [filterDimensionSet, value],
  );
  const convertedAdvancedFilterValue = React.useMemo(
    () =>
      advancedFilterValue
        ? convertCustomTagToDimension(filterDimensionSet, advancedFilterValue)
        : advancedFilterValue,
    [filterDimensionSet, advancedFilterValue],
  );

  // Initialize the store
  if (!store.current) {
    store.current = createStore();
    store.current.set(filterReadonlyContextAtom, {
      meta,
      advancedFilterValue: convertedAdvancedFilterValue,
      ...p,
    });
    store.current.set(defaultFilterValue, convertedValue);
    store.current.set(filterValueAtom, convertedValue);
    store.current.set(filterStatusAtom, 'idle');
    store.current.set(loadingAtom, false);
    store.current.set(appendedAtomStatus, defaultAppendedAtomStatus);
    store.current.set(rulesExpandedAtom, defaultRulesExpanded);
  }

  React.useEffect(() => {
    store.current!.set(defaultFilterValue, convertedValue);
    store.current!.set(filterValueAtom, convertedValue);
    store.current!.set(filterStatusAtom, 'idle');
  }, [convertedValue]);

  React.useEffect(() => {
    store.current!.set(filterReadonlyContextAtom, {
      meta,
      advancedFilterValue: convertedAdvancedFilterValue,
      ...p,
    });
  }, [meta, convertedAdvancedFilterValue, p]);

  React.useImperativeHandle(
    ref,
    () => ({
      getInnerStatus: () => store.current!.get(filterStatusAtom),
      getInnerFilterValue: () => store.current!.get(filterValueAtom),
      updateInnerStatus: (value) => {
        store.current!.set(filterStatusAtom, value);
      },
      updateInnerFilterValue: (value) => {
        store.current!.set(filterValueAtom, value);
      },
    }),
    [],
  );

  React.useEffect(() => {
    return store.current?.sub(rulesExpandedAtom, () => {
      onRulesExpandChange?.(store.current!.get(rulesExpandedAtom));
    });
  }, [onRulesExpandChange]);

  React.useEffect(() => {
    return store.current?.sub(appendedAtomStatus, () => {
      onAppendedAtomStatusChange?.(store.current!.get(appendedAtomStatus));
    });
  }, [onAppendedAtomStatusChange]);

  return <Provider store={store.current}>{children}</Provider>;
});

FilterDialogProvider.displayName = 'FilterDialogProvider';
